import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Mail, LogOut, Settings, Bell, Shield, Crown, Sparkles, ChevronRight } from 'lucide-react';
import Navigation from '@/components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUICK_TABS = [
  { path: '/hr-zone', label: 'HR Zone', desc: 'Daily MLB home run projections', emoji: '⚾' },
  { path: '/wnba/hot-props', label: 'WNBA Hot Props', desc: 'Top WNBA prop leaders by category', emoji: '🏀' },
  { path: '/props-picks', label: 'Free Props Picks', desc: 'Lock of the Day + 10 daily parlays', emoji: '🎟️' },
];

const Profile = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    navigate('/');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />

      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Profile Header */}
          <Card className="bg-[#0f1e36] border-[#1e3a5f] mb-8">
            <CardContent className="p-8">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <Avatar className="h-24 w-24 border-2 border-brand-cyan">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-[#112240] text-white text-2xl font-heading">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>

                <div className="text-center sm:text-left">
                  <h1 className="font-heading text-3xl font-bold text-white uppercase tracking-tight">
                    {user?.name || 'User'}
                  </h1>
                  <p className="text-muted-foreground mt-1 flex items-center gap-2 justify-center sm:justify-start">
                    <Mail className="h-4 w-4" />
                    {user?.email || 'No email'}
                  </p>
                  <div className="mt-3 flex items-center gap-2 justify-center sm:justify-start">
                    <span className="px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1.5 bg-brand-lime/10 text-brand-lime" data-testid="plan-badge">
                      <Crown className="h-3.5 w-3.5" />
                      {isAdmin ? 'Admin — Full Access' : 'Free — Full Access'}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Settings */}
          <Card className="bg-[#0f1e36] border-[#1e3a5f] mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-xl font-bold text-white uppercase tracking-wide flex items-center gap-2">
                <Settings className="h-5 w-5 text-brand-cyan" />
                Account Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-[#0a1628] rounded-md border border-[#1e3a5f]">
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <div className="text-white font-semibold">Profile Information</div>
                    <div className="text-sm text-muted-foreground">
                      Manage your personal details
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-[#1e3a5f] text-white hover:bg-[#112240]"
                  disabled
                >
                  Edit
                </Button>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#0a1628] rounded-md border border-[#1e3a5f]">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <div className="text-white font-semibold">Notifications</div>
                    <div className="text-sm text-muted-foreground">
                      Configure alert preferences
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-[#1e3a5f] text-white hover:bg-[#112240]"
                  disabled
                >
                  Configure
                </Button>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#0a1628] rounded-md border border-[#1e3a5f]">
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <div className="text-white font-semibold">Privacy & Security</div>
                    <div className="text-sm text-muted-foreground">
                      Manage your account security
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-[#1e3a5f] text-white hover:bg-[#112240]"
                  disabled
                >
                  Manage
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Popular tabs */}
          <Card className="bg-[#0f1e36] border-[#1e3a5f] mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-xl font-bold text-white uppercase tracking-wide flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-brand-lime" />
                Popular Tabs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">Everything on Free Props is 100% free — jump right in.</p>
              <div className="grid sm:grid-cols-3 gap-3" data-testid="free-tabs-section">
                {QUICK_TABS.map((t) => (
                  <button
                    key={t.path}
                    onClick={() => navigate(t.path)}
                    className="text-left p-4 bg-[#0a1628] rounded-md border border-[#1e3a5f] hover:border-brand-lime/60 transition-colors group"
                    data-testid={`free-tab-btn-${t.path.replace(/\//g, '-').slice(1)}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xl">{t.emoji}</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-brand-lime" />
                    </div>
                    <div className="text-white font-semibold mt-2">{t.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">{t.desc}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="bg-[#0f1e36] border-[#1e3a5f]">
            <CardHeader>
              <CardTitle className="font-heading text-xl font-bold text-red-400 uppercase tracking-wide">
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-[#0a1628] rounded-md border border-red-500/30">
                <div>
                  <div className="text-white font-semibold">Sign Out</div>
                  <div className="text-sm text-muted-foreground">
                    Sign out of your account on this device
                  </div>
                </div>
                <Button
                  variant="destructive"
                  onClick={handleLogout}
                  className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Profile;
